
import { useState, useEffect } from 'react';
import {
  TextField,
  Button,
  Typography,
  MenuItem,
  Box,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';

import { useNavigate } from 'react-router-dom';

const EntryForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    customerName: '',
    place: '',
    date: '',
    time: '',
    serialNo: '',
    systemName: '',

    paymentStatus: 'Pending',
    customerId: '',
    address: '',
    customerIssue: '',
  });
  const [customerIdExists, setCustomerIdExists] = useState(false);
  const [isCheckingCustomerId, setIsCheckingCustomerId] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);

  useEffect(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');

    setFormData((prevData) => ({
      ...prevData,
      date: `${year}-${month}-${day}`,
      time: `${hours}:${minutes}`,
    }));
  }, []);

  const handleCustomerIdBlur = async () => {
    if (formData.customerId) {
      setIsCheckingCustomerId(true);
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/entries/check-customer-id/${formData.customerId}`);
        const data = await response.json();
        setCustomerIdExists(data.exists);
      } catch (error) {
        console.error('Error checking customer ID:', error);
      }
      setIsCheckingCustomerId(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/entries`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      const data = await response.json();
      console.log(data);
      setOpenSnackbar(true);
      setTimeout(() => {
        navigate('/systems');
      }, 2000);
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={2000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity="success" sx={{ width: '100%' }}>
          Entry saved successfully!
        </Alert>
      </Snackbar>
      <Typography variant="h4" gutterBottom>
        Add New System Entry
      </Typography>
      <form onSubmit={handleSubmit}>
        <Box sx={{ mt: 1 }}>
          <Box sx={{ mb: 2 }}>
            <TextField
              required
              id="customerName"
              name="customerName"
              label="Customer Name"
              fullWidth
              value={formData.customerName}
              onChange={handleChange}
            />
          </Box>
          <Box sx={{ mb: 2 }}>
            <TextField
              required
              id="place"
              name="place"
              label="Place"
              fullWidth
              value={formData.place}
              onChange={handleChange}
            />
          </Box>
          <Box sx={{ mb: 2 }}>
            <TextField
              required
              id="date"
              name="date"
              label="Date"
              type="date"
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
              InputProps={{
                readOnly: true,
              }}
              value={formData.date}
              onChange={handleChange}
            />
          </Box>
          <Box sx={{ mb: 2 }}>
            <TextField
              required
              id="time"
              name="time"
              label="Time"
              type="time"
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
              InputProps={{
                readOnly: true,
              }}
              value={formData.time}
              onChange={handleChange}
            />
          </Box>
          <Box sx={{ mb: 2 }}>
            <TextField
              required
              id="serialNo"
              name="serialNo"
              label="Serial No"
              fullWidth
              value={formData.serialNo}
              onChange={handleChange}
            />
          </Box>
          <Box sx={{ mb: 2 }}>
            <TextField
              required
              id="systemName"
              name="systemName"
              label="System Name"
              fullWidth
              value={formData.systemName}
              onChange={handleChange}
            />
          </Box>
          <Box sx={{ mb: 2 }}>
            <TextField
              required
              id="contactNumber"
              name="contactNumber"
              label="Contact Number"
              type="number"
              fullWidth
              value={formData.contactNumber}
              onChange={handleChange}
            />
          </Box>
          <Box sx={{ mb: 2 }}>
            <TextField
              required
              id="customerId"
              name="customerId"
              label="Customer ID"
              type="number"
              fullWidth
              value={formData.customerId}
              onChange={handleChange}
              onBlur={handleCustomerIdBlur}
              error={customerIdExists}
              helperText={customerIdExists ? 'Customer ID already exists.' : ''}
              InputProps={{
                endAdornment: isCheckingCustomerId && <CircularProgress size={20} />,
              }}
            />
          </Box>
          <Box sx={{ mb: 2 }}>
            <TextField
              required
              id="address"
              name="address"
              label="Address"
              fullWidth
              value={formData.address}
              onChange={handleChange}
            />
          </Box>
          <Box sx={{ mb: 2 }}>
            <TextField
              id="paymentStatus"
              name="paymentStatus"
              label="Payment Status"
              select
              fullWidth
              value={formData.paymentStatus}
              onChange={handleChange}
            >
              <MenuItem value="Pending">Pending</MenuItem>
              <MenuItem value="Paid">Paid</MenuItem>
            </TextField>
          </Box>

          <Box sx={{ mb: 2 }}>
            <TextField
              required
              id="customerIssue"
              name="customerIssue"
              label="Customer Issue"
              fullWidth
              value={formData.customerIssue}
              onChange={handleChange}
            />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          <Button onClick={() => navigate('/systems')} sx={{ mr: 1 }}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" color="primary" disabled={customerIdExists || isCheckingCustomerId}>
            Save
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default EntryForm;

